<template>
  <div class="pricing">
    <Popup v-model="showSkipPopup" popupHeader="Skip for now?">
      <p class="text-body">You will need to upgrade to connect with sitters</p>
      <Button value="Skip" class="w-full mt-16" size="medium" :href="upgradeLaterLink" />
      <Button value="Upgrade Now" class="w-full mt-16" size="medium" variant="outlined" :href="upgradeRedirectLink" />
    </Popup>
    <AlertBanner v-if="renderAlert" :status="0" class="w-full sticky z-[5]">
      <span>Company not found. You may not be eligible for Premium through employer benefits.</span>
    </AlertBanner>
    <div class="flex-col lg:flex-row justify-between relative pt-16 md:pt-64">
      <img
        class="absolute hidden w-full object-cover lg:block top-[-200px] left-0"
        src="@/images/upgrade/desktop-pricing-header.svg"
        aria-hidden="true"
      />
      <img
        class="absolute w-full object-cover hidden md:block lg:hidden top-0 left-0"
        src="@/images/upgrade/tablet-pricing-header.svg"
        aria-hidden="true"
      />
      <img
        class="absolute w-full object-cover block md:hidden -top-[100px] left-0"
        src="@/images/upgrade/mobile-pricing-header.svg"
        aria-hidden="true"
      />
      <div class="z-[5] relative mx-auto px-16 max-w-580 lg:max-w-928">
        <p :class="`${recentlyViewedCaregiverDisplayName ? '' : 'text-left sm:text-center lg:text-left'} text-[20px]`">
          The best sitters <span class="text-primary">love</span> Sittercity
        </p>
        <div
          :class="`flex ${recentlyViewedCaregiverDisplayName ? 'flex-row' : 'flex-col'} lg:flex-row justify-between items-center`"
        >
          <div :class="`w-full sm:w-auto mt-16 lg:mt-8 md:mb-32 lg:mb-82 ${recentlyViewedCaregiverDisplayName ? 'mb-64' : ''}`">
            <div v-if="isLoading">
              <Skeleton custom-class="h-[26px] md:h-32 lg:h-56 w-320 md:w-488 lg:w-392 mb-8" />
              <Skeleton custom-class="hidden lg:block lg:h-56 w-3/4" />
            </div>
            <h1 v-else :class="`text-[24px] md:text-l font-medium lg:text-xl`">
              {{
                recentlyViewedCaregiverDisplayName
                  ? `Upgrade to contact ${recentlyViewedCaregiverDisplayName}`
                  : 'Upgrade to find the care you need'
              }}
            </h1>
          </div>

          <Skeleton v-if="isLoading" rounded="full" custom-class="h-64 w-64 md:h-[140px] md:w-[140px] translate-y-[-20%]" />
          <img
            v-else-if="recentlyViewedCaregiverPhoto"
            class="max-w-64 w-full md:max-w-[140px] translate-y-[-20%] rounded-full ml-12"
            :src="recentlyViewedCaregiverPhoto"
            alt="Previous viewed caregiver"
          />

          <div v-else class="flex flex-col flex-shrink-0 md:ml-16 mt-18 md:mt-0 mb-48">
            <div class="flex mx-auto">
              <img
                class="pricing-header__sample-sitters hidden lg:block"
                :src="desktopSampleSitters"
                :alt="sampleSitterAltText"
              />
              <img
                class="pricing-header__sample-sitters lg:hidden justify-self-center"
                :src="mobileSampleSitters"
                :alt="sampleSitterAltText"
              />
            </div>
            <p class="pricing-header__sitter-caption mt-12 lg:mt-8">
              <!-- TODO: revert after investigating inflated results -->
              <!-- We're showing your job to <span>{{ sitterNumber }}</span> local {{ careTypeText }} -->
              We're showing your job to local {{ careTypeText }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="main-content mx-auto max-w-580 lg:max-w-928 z-[5] relative px-16">
      <div class="plans flex flex-col">
        <h2 class="text-m2 lg:text-m3 font-medium z-[1]">Choose your plan</h2>
        <div v-if="isLoading" class="flex flex-col lg:flex-row justify-between mt-12 lg:mt-32">
          <PlanCardSkeleton v-for="i in 3" :key="i" class="mt-8 md:mt-32 lg:mt-0" />
        </div>
        <div v-else class="flex flex-col lg:flex-row justify-between mt-12 lg:mt-32">
          <Card
            v-for="(plan, i) in paymentPlanOptions"
            :key="plan.title + i"
            :aria-label="getCardAriaLabel(plan)"
            :clickable="true"
            :class="[plan.planCode === selectedPlan && 'active-card', 'mt-8 md:mt-32 lg:mt-0']"
            class="plan-card flex min-h-96 px-6 py-20 md:p-22 border-solid shadow-none"
            @click="() => (selectedPlan = plan.planCode)"
          >
            <p v-if="plan.monthsFree" class="plan-card__box-label border-2 border-solid rounded-md bg-primary-subdued px-8 py-2">
              {{ plan.monthsFree }} months free
            </p>
            <p
              v-else-if="plan.bestValue"
              class="plan-card__box-label border-2 border-solid rounded-md bg-primary-subdued px-8 py-2"
            >
              Best value
            </p>
            <RadioButton
              :id="plan.planCode + '-radio-button'"
              v-model="selectedPlan"
              :checked="plan.planCode === selectedPlan"
              :radioValue="plan.planCode"
              :radioName="plan.planCode"
              :labelName="''"
              :tabIndex="-1"
            />
            <div class="flex lg:flex-col justify-between lg:justify-start lg:items-start w-full">
              <h2 class="text-m md:text-m2 lg:text-m3 font-medium pt-8 md:pt-4">{{ plan.title }}</h2>
              <div v-if="plan.price" class="flex flex-col items-end lg:items-start text-right lg:text-left">
                <div class="flex flex-col-reverse xs:flex-row lg:mt-32">
                  <p class="font-medium flex items-baseline">
                    <span class="text-s md:text-m self-center">$</span>
                    <span class="text-m xs:text-m2 md:text-l font-medium">{{
                      plan.price % 1 === 0 ? plan.price : plan.price.toFixed(2)
                    }}</span>
                    <span class="plan-card__dark-grey-text text-xs xs:text-s font-regular mb-2">{{ pricePerMonthLabel }}</span>
                  </p>
                </div>
                <p
                  v-if="plan.planCode !== 'monthly'"
                  :class="plan.planCode === 'annual' ? 'plan-card__green-text' : 'plan-card__grey-text'"
                  class="text-xs xs:text-s self-end"
                >
                  {{ getPlanCardSubText(plan.price) }}
                </p>
              </div>
            </div>
          </Card>
        </div>
        <Button value="Continue" size="large" class="w-full lg:w-auto mx-auto my-32" :href="upgradeRedirectLink" />
        <p class="text-xs">
          Subscriptions are non-refundable and automatically renew until cancelled by downgrading to a basic account in account
          settings. To cancel, update your account settings, call <a href="tel:+18887482489" class="underline">888-748-2489</a>,
          or email <a href="mailto:support@sittercity.com" class="underline">support@sittercity.com</a> before the end of the
          current subscription term.
        </p>
      </div>

      <div class="feature-table mt-72">
        <Card class="feature-table__card flex flex-col lg:flex-row w-full justify-between" :clickable="false">
          <h2 class="font-medium text-m3 lg:w-1/2 lg:min-w-424 mb-16 md:mb-48 lg:mb-0 lg:pr-80">
            Access all of Sittercity with Premium
          </h2>
          <FeatureTable :tableHeadings="['What you get', 'Premium', 'Current']">
            <FeatureTableRow v-for="(row, index) in featureTableRows" :key="row.title" :index="index" class="text-center">
              <td scope="row" :class="index % 2 === 0 ? 'py-10' : 'py-12'" class="pl-16 text-left text-m">
                {{ row.title }}
              </td>
              <td scope="row">
                <TickIcon :class="iconStyles" alt="available" :width="24" :height="24" />
              </td>
              <td scope="row">
                <TickIcon v-if="row.secondColumn === true" :class="iconStyles" alt="available" :width="24" :height="24" />
                <LockIcon v-else :class="iconStyles" alt="locked" color="#8A8A8F" />
              </td>
            </FeatureTableRow>
          </FeatureTable>
        </Card>
      </div>

      <div class="bh-card flex flex-col-reverse lg:flex-row justify-between w-full mb-48 lg:mb-80 mt-64 rounded-xl p-24">
        <div class="flex flex-col justify-between">
          <h3 class="text-m1 lg:text-m2 font-medium text-white mt-20 lg:mt-0">Do your employer benefits include Sittercity?</h3>
          <Button
            class="w-full mt-20 md:mt-24 lg:mt-0"
            value="Check eligibility"
            :href="'/bh-benefits/upgrade?redirectTo=%2Fupgrade%2F' + 'pricing'"
            variant="secondary"
            size="medium"
            :preventDefault="true"
            @click="eligibilityButtonClick()"
          />
        </div>
        <img src="@/images/bh-benefits/bh-logo-white.svg" class="w-212 lg:w-226 mr-20" alt="Bright Horizons logo" />
      </div>
      <h2 class="font-medium text-m1 lg:text-m3 mt-48 md:mt-64 lg:mt-80">Premium parents who love Sittercity</h2>
    </div>

    <div class="w-full overflow-hidden">
      <TestimonialsCarousel />

      <div class="relative w-full">
        <div class="upgrade-later-section absolute bottom-48 lg:bottom-72 inset-x-0 mx-auto">
          <h3 class="text-m1 lg:text-m2 font-medium">Not ready to find care yet?</h3>
          <Card class="mt-8 lg:mt-16 lg:flex justify-between items-center">
            Skip for now to browse sitters and nannies and upgrade for full access at any&nbsp;time
            <Button
              variant="outlined"
              value="Skip"
              size="medium"
              class="w-full lg:w-auto my-auto lg:ml-76 mt-20 lg:mt-0"
              :href="upgradeLaterLink"
            />
          </Card>
        </div>

        <img class="w-full hidden lg:block mt-56" src="@/images/upgrade/desktop-pricing-footer.svg" aria-hidden="true" />
        <img class="w-full hidden md:block lg:hidden mt-56" src="@/images/upgrade/tablet-pricing-footer.svg" aria-hidden="true" />
        <img class="w-full md:hidden mt-56" src="@/images/upgrade/mobile-pricing-footer.svg" aria-hidden="true" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { subDays, format } from 'date-fns';
import TestimonialsCarousel from './TestimonialsCarousel.vue';
import PlanCardSkeleton from './PlanCardSkeleton.vue';

import { sendSegmentEventWithTimeout } from '@/lib/segment';
import { getFeatureValue } from '@/lib/growthbook';
import { STORAGE_KEYS, getItem } from '@/lib/storage';
import { getCouponCode, buildUpgradeRedirectLink, persistCouponCode } from './helpers/upgradeHelpers';
import { getParentJobsURL } from '@/helpers/bookings/bookingsUrlHelper';

import AlertBanner from '../../components/alert_banner/AlertBanner.vue';
import Button from '../../components/button/Button.vue';
import Card from '../../components/card/Card.vue';
import FeatureTableRow from '../../components/feature_table/FeatureTableRow.vue';
import FeatureTable from '../../components/feature_table/FeatureTable.vue';
import Popup from '../../components/popup/Popup.vue';
import RadioButton from '../../components/radio_button/RadioButton.vue';
import Skeleton from '../../components/skeleton/Skeleton.vue';

import TickIcon from '@/graphics/icons/TickIcon.vue';
import LockIcon from '@/graphics/icons/LockIcon.vue';

import SITTER_COUNT_QUERY, {
  PARENT_CARE_TYPE_QUERY,
  SITTER_PHOTO_QUERY,
  PAYMENT_PLANS_QUERY,
} from '../../graphql/upgrade/queries';

interface PaymentPlan {
  planCode: string;
  title: string;
  bestValue?: boolean;
  monthsFree?: number;
  price: number;
}

export default {
  name: 'Pricing',
  components: {
    TestimonialsCarousel,
    PlanCardSkeleton,

    AlertBanner,
    Button,
    Card,
    Skeleton,
    FeatureTableRow,
    FeatureTable,
    RadioButton,
    Popup,

    TickIcon,
    LockIcon,
  },
  data(): any {
    return {
      paymentPlans: null,
      careType: '',
      sitterCount: null,
      selectedPlan: 'annual',
      showSkipPopup: false,
      recentlyViewedCaregiver: null,
      viewerLoading: true,
      navClickHandlers: [] as Array<{ element: HTMLElement; handler: () => void }>,
      segmentEventTracked: false,
      plansLoading: true,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.plansLoading || this.viewerLoading;
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    recentlyViewedCaregiverId(): string | null {
      return getItem(STORAGE_KEYS.RECENTLY_VIEWED_PROVIDER_ID, 'local');
    },
    recentlyViewedCaregiverPhoto(): string {
      return this.recentlyViewedCaregiver?.photoUrl || '';
    },
    recentlyViewedCaregiverDisplayName(): string {
      return this.recentlyViewedCaregiver?.displayName || '';
    },
    couponCode(): string | undefined {
      return getCouponCode(this.$route.query);
    },
    upgradeRedirectLink(): string {
      return buildUpgradeRedirectLink(this.selectedPlan, this.careType, this.couponCode, this.$route.query);
    },
    paymentPlanOptions(): PaymentPlan[] {
      if (!this.paymentPlans) {
        return [];
      }
      return [
        {
          planCode: 'monthly',
          title: '1-month subscription',
          // prettier-ignore
          price: this.paymentPlans?.find((plan) => ['default-monthly', 'high-monthly', 'moderate-monthly'].includes(plan.planCode))?.price / 100,
        },
        {
          planCode: 'quarterly',
          title: '3-month subscription',
          // prettier-ignore
          price: this.paymentPlans?.find((plan) => ['default-quarterly', 'high-quarterly', 'moderate-quarterly'].includes(plan.planCode))?.price / 3 / 100,
        },

        {
          planCode: 'annual',
          title: '12-month subscription',
          monthsFree: 7,
          bestValue: true,
          // prettier-ignore
          price: this.paymentPlans?.find((plan) => ['default-annual', 'high-annual', 'moderate-annual'].includes(plan.planCode))?.price / 12 / 100,
        },
      ];
    },
    sitterNumber(): string {
      return this.sitterCount >= 10 ? this.sitterCount : '';
    },
    desktopSampleSitters(): string {
      return 'https://images.ctfassets.net/wmwzystk5o45/drnnZpKzwV3bHDyCwVMI0/086b1e747d9cb94bdd01078e77ec7da1/Avatar-_desktop.webp';
    },
    mobileSampleSitters(): string {
      return 'https://images.ctfassets.net/wmwzystk5o45/10gV7a4GDLLvTAYhCOleQp/b4b812d52338a7a06bd671d33f54a621/Avatar-mobile.webp';
    },
    sampleSitterAltText(): string {
      return 'Four sitter profile photos';
    },
    bgCheckAltText(): string {
      return 'background check icon';
    },
    featureTableRows(): { title: string; firstColumn: boolean; secondColumn: boolean }[] {
      return [
        { title: 'Post a job', firstColumn: true, secondColumn: true },
        { title: 'Unlimited sitter search', firstColumn: true, secondColumn: true },
        { title: 'Read applications', firstColumn: true, secondColumn: true },
        { title: 'Message sitters', firstColumn: true, secondColumn: false },
        { title: 'Request interviews', firstColumn: true, secondColumn: false },
        { title: 'Book unique activities', firstColumn: true, secondColumn: false },
      ];
    },
    iconStyles(): string {
      return 'inline-block align-middle h-12 md:h-20';
    },
    renderAlert(): boolean {
      return window.location.toString().includes('error');
    },
    pricePerMonthLabel(): string {
      const priceSegment = getFeatureValue('sc-price-segment', 'default');
      return priceSegment && priceSegment !== 'default' ? '/mo' : '/month';
    },
    upgradeLaterLink(): string {
      const hasPhoto = this.account?.originalPhoto !== null;
      const careType = this.careType || 'babysitter';
      const zipcode = this.$store.getters['account/getZipCode'] || this.account?.location?.zipcode || '';

      return hasPhoto ? getParentJobsURL(careType.toUpperCase(), zipcode) : '/account/seekers/photo#/upload';
    },
    lastLoginAt(): string {
      const d = subDays(new Date(), 42);
      return format(d, 'yyyy-MM-dd');
    },
    careTypeText(): string {
      switch (this.careType) {
        case 'babysitter':
          return 'sitters';
        case 'nanny':
          return 'nannies';
        case 'pet_care':
          return 'pet sitters';
        case 'companion_care':
          return 'caregivers';
        case 'housekeeping':
          return 'housekeepers';
        default:
          return 'sitters';
      }
    },
  },
  created() {
    const routePromo = this.$route.query.promo as string | undefined;
    if (routePromo) {
      persistCouponCode(routePromo);
    }
  },
  mounted() {
    this.addHomeLinkToNav();
  },
  beforeDestroy() {
    this.navClickHandlers.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
    });
    this.navClickHandlers = [];
  },
  methods: {
    addHomeLinkToNav(): void {
      const desktopNavHomeAnchorElement = document.querySelector('a.sittercity-nav-logo') as HTMLAnchorElement;
      const mobileNavHomeAnchorElement = document.querySelector('nav [alt="Sittercity"]') as HTMLElement;

      if (desktopNavHomeAnchorElement) {
        desktopNavHomeAnchorElement.href = '#';
        desktopNavHomeAnchorElement.addEventListener('click', this.popupOpen);
        this.navClickHandlers.push({ element: desktopNavHomeAnchorElement, handler: this.popupOpen });
      }

      if (mobileNavHomeAnchorElement) {
        mobileNavHomeAnchorElement.addEventListener('click', this.popupOpen);
        this.navClickHandlers.push({ element: mobileNavHomeAnchorElement, handler: this.popupOpen });
      }
    },
    popupOpen(): void {
      this.showSkipPopup = true;
    },
    getCardAriaLabel(plan) {
      const label = `${plan.title + plan.price}dollars per month`;

      if (plan.planCode !== 'monthly') {
        return label.concat(`12 months for${plan.price * 12}dollars`);
      }
      return label;
    },
    getPlanCardSubText(planPrice) {
      const monthlyPrice = this.paymentPlanOptions.filter((plan) => plan.planCode === 'monthly')[0].price;
      const discPercentage = ((monthlyPrice - planPrice) / monthlyPrice) * 100;

      return `(Save ${Math.ceil(discPercentage)}%)`;
    },
    trackPremiumUpgradeStarted() {
      if (this.segmentEventTracked) {
        return;
      }
      this.segmentEventTracked = true;
      sendSegmentEventWithTimeout('Premium Upgrade Started', { care_type: this.careType });
      sendSegmentEventWithTimeout('Criteo Intent Displayed', { product_id: '1' });
      sendSegmentEventWithTimeout('Criteo Premium Upgrade Started', {
        products: [
          {
            product_id: '1',
            quantity: '1',
            price: '0',
          },
        ],
      });
    },
    async eligibilityButtonClick() {
      const date = new Date().toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' });
      const time = new Date().toLocaleTimeString('default', { hour: 'numeric', minute: 'numeric', second: 'numeric' });
      const path = this.$route.query;

      await sendSegmentEventWithTimeout('CorporateBenefit_CheckEligibility', {
        click_date: date,
        time_stamp: time,
        url: path,
      });

      window.location.href = '/bh-benefits/upgrade?redirectTo=%2Fupgrade%2Fpricing';
    },
  },
  apollo: {
    viewer: {
      query: PARENT_CARE_TYPE_QUERY,
      result({ data, loading }) {
        if (!loading && data?.viewer?.careType) {
          this.careType = data?.viewer?.careType.toLowerCase();
        }
        if (!loading) {
          this.viewerLoading = false;
          this.trackPremiumUpgradeStarted();
        }
      },
      error() {
        this.hasError = true;
        // Still allow component to render even if careType query fails
        this.viewerLoading = false;
        this.trackPremiumUpgradeStarted();
      },
    },
    sitterSearch: {
      query: SITTER_COUNT_QUERY,
      skip() {
        return !this.careType;
      },
      variables(): any {
        return {
          careType: this.careType,
          geoPointX: this.account.location.longitude.toString(),
          geoPointY: this.account.location.latitude.toString(),
          distance: 10,
          lastLoginAt: this.lastLoginAt,
        };
      },
      result({ data, loading }) {
        if (!loading) {
          this.sitterCount = data.sitterSearch.totalCount;
        }
      },
      error() {
        this.hasError = true;
      },
    },
    sitterAccount: {
      query: SITTER_PHOTO_QUERY,
      skip() {
        return !this.recentlyViewedCaregiverId;
      },
      variables(): any {
        return {
          accountId: this.recentlyViewedCaregiverId,
        };
      },
      result({ data, loading }) {
        if (!loading && data?.sitterAccount) this.recentlyViewedCaregiver = data.sitterAccount;
      },
      error() {
        console.error('Error fetching sitter photo data');
      },
    },
    paymentPlans: {
      query: PAYMENT_PLANS_QUERY,
      result({ data, loading }) {
        if (!loading && data?.paymentPlans) {
          this.paymentPlans = data.paymentPlans;
          this.plansLoading = false;
        }
      },
      error() {
        console.error('Error fetching payment plans');
        this.plansLoading = false;
      },
    },
  },
};
</script>
